import {
  type AllocatePaymentInput,
  type ChequeActionInput,
  type Invoice,
  type PaymentInput,
  type PaymentMethod,
  type PaymentRecord,
  type Rial,
} from "@kitchen/schemas";
import { Button, Chip, faDigits, formatDate, formatMoney } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, financeApi, type TreeNode } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: "نقدی",
  card: "کارتخوان",
  "bank-transfer": "واریز به حساب",
  cheque: "چک صیادی",
};

export function PaymentsTab() {
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");

  // Record Payment Modal State
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [payOrgId, setPayOrgId] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>("bank-transfer");
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payRef, setPayRef] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [chequeNum, setChequeNum] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [chequeDue, setChequeDue] = useState("");

  // Cheque Bounce Modal State
  const [bouncingCheque, setBouncingCheque] = useState<PaymentRecord | null>(null);
  const [bounceReason, setBounceReason] = useState("");

  // Allocation Modal State
  const [allocatingPayment, setAllocatingPayment] = useState<PaymentRecord | null>(null);
  const [allocAmounts, setAllocAmounts] = useState<Record<string, number>>({});

  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });

  const orgList: { id: string; name: string }[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      orgList.push({ id: n.id, name: n.name });
      walk(n.children);
    }
  };
  walk(tree.data ?? []);

  const getOrgName = (id: string) => orgList.find((o) => o.id === id)?.name ?? "سازمان";

  const paymentsQuery = useQuery({
    queryKey: ["finance", "payments", selectedOrgId],
    queryFn: () => financeApi.listPayments(selectedOrgId || undefined),
  });

  // Query open invoices for the allocating organization
  const openInvoicesQuery = useQuery({
    queryKey: ["finance", "invoices", allocatingPayment?.organizationId, "issued"],
    queryFn: () =>
      allocatingPayment
        ? financeApi.listInvoices({ organizationId: allocatingPayment.organizationId, status: "issued" })
        : Promise.resolve([] as Invoice[]),
    enabled: !!allocatingPayment,
  });

  // Action: Record Payment
  const recordAction = useAction(
    async (input: PaymentInput) => {
      await financeApi.recordPayment(input);
      setShowRecordModal(false);
      setPayOrgId("");
      setPayAmount(0);
      setPayRef("");
      setPayNotes("");
      setChequeNum("");
      setChequeBank("");
      setChequeDue("");
    },
    [
      ["finance", "payments"],
      ["finance", "statement"],
    ],
  );

  // Action: Update Cheque Status
  const chequeStatusAction = useAction(
    async ({ id, input }: { id: string; input: ChequeActionInput }) => {
      await financeApi.updateChequeStatus(id, input);
      setBouncingCheque(null);
      setBounceReason("");
    },
    [
      ["finance", "payments"],
      ["finance", "statement"],
    ],
  );

  // Action: Allocate Payment
  const allocateAction = useAction(
    async (input: AllocatePaymentInput) => {
      await financeApi.allocatePayment(input);
      setAllocatingPayment(null);
      setAllocAmounts({});
    },
    [
      ["finance", "payments"],
      ["finance", "statement"],
    ],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Top Bar & Filters */}
      <div className="panel">
        <div className="row wrap-between" style={{ alignItems: "center", gap: "var(--s3)" }}>
          <div className="row wrap" style={{ alignItems: "center", gap: "var(--s3)" }}>
            <div>
              <label htmlFor="pay-filter-org" className="label t-cap muted">
                سازمان
              </label>
              <select
                id="pay-filter-org"
                className="input"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                }}
              >
                <option value="">همه سازمان‌ها</option>
                {orgList.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Button
            variant="primary"
            icon="wallet"
            onClick={() => {
              setShowRecordModal(true);
            }}
          >
            ثبت دریافت وجه جدید
          </Button>
        </div>
      </div>

      {/* Payments List */}
      <div className="panel" title="فهرست دریافت‌ها و وصولی‌ها">
        {paymentsQuery.isLoading && <p className="muted">در حال بارگذاری دریافت‌ها...</p>}
        {paymentsQuery.isError && <ErrorLine error="خطا در دریافت فهرست پرداخت‌ها" />}

        {paymentsQuery.data && paymentsQuery.data.length === 0 && (
          <p className="muted text-center" style={{ padding: "var(--s6) 0" }}>
            دریافتی با فیلترهای مشخص‌شده ثبت نشده است.
          </p>
        )}

        {paymentsQuery.data && paymentsQuery.data.length > 0 && (
          <table className="table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>تاریخ پرداخت</th>
                <th>سازمان</th>
                <th>روش پرداخت</th>
                <th>مبلغ (ریال)</th>
                <th>مانده تخصیص‌نیافته</th>
                <th>شماره پیگیری / چک</th>
                <th>وضعیت وصول</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {paymentsQuery.data.map((p) => (
                <tr key={p.id}>
                  <td>{formatDate(p.paidAt)}</td>
                  <td className="bold">{getOrgName(p.organizationId)}</td>
                  <td>
                    <Chip plain>{METHOD_LABELS[p.method]}</Chip>
                  </td>
                  <td className="bold">{formatMoney(p.amount, "rial")}</td>
                  <td>
                    {p.unallocatedAmount > 0 ? (
                      <span className="bold" style={{ color: "var(--blue-ink)" }}>
                        {formatMoney(p.unallocatedAmount, "rial")}
                      </span>
                    ) : (
                      <span className="muted">تماماً تخصیص داده شده</span>
                    )}
                  </td>
                  <td>
                    {p.method === "cheque"
                      ? `${p.chequeNumber ?? "—"} (${p.chequeBank ?? "بانک"})`
                      : p.referenceNumber || "—"}
                  </td>
                  <td>
                    {p.method === "cheque" ? (
                      p.chequeStatus === "cleared" ? (
                        <Chip tone="ok">وصول شد</Chip>
                      ) : p.chequeStatus === "bounced" ? (
                        <Chip tone="coral">برگشت خورده</Chip>
                      ) : (
                        <Chip tone="warn">در انتظار وصول ({formatDate(p.chequeDueDate ?? p.paidAt)})</Chip>
                      )
                    ) : (
                      <Chip tone="ok">واریز قطعی</Chip>
                    )}
                  </td>
                  <td>
                    <div className="row" style={{ gap: "var(--s2)" }}>
                      {p.method === "cheque" && p.chequeStatus === "pending" && (
                        <>
                          <Button
                            size="md"
                            variant="default"
                            onClick={() => {
                              chequeStatusAction.run({
                                id: p.id,
                                input: { version: p.version, action: "clear" },
                              });
                            }}
                          >
                            وصول شد
                          </Button>
                          <Button
                            size="md"
                            variant="urgent"
                            onClick={() => {
                              setBouncingCheque(p);
                              setBounceReason("");
                            }}
                          >
                            برگشت
                          </Button>
                        </>
                      )}

                      {p.unallocatedAmount > 0 && p.chequeStatus !== "bounced" && (
                        <Button
                          size="md"
                          variant="ghost"
                          onClick={() => {
                            setAllocatingPayment(p);
                            setAllocAmounts({});
                          }}
                        >
                          تخصیص به فاکتور
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal: Record New Payment */}
      {showRecordModal && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "580px", width: "90%" }}>
            <h2 className="t-title">ثبت دریافت وجه جدید</h2>
            <p className="muted t-cap">دریافت وجه نقد، کارتخوان، فیش واریزی یا چک از مشتری.</p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div>
                <label htmlFor="rec-org" className="label t-cap">
                  انتخاب سازمان
                </label>
                <select
                  id="rec-org"
                  className="input"
                  value={payOrgId}
                  onChange={(e) => {
                    setPayOrgId(e.target.value);
                  }}
                >
                  <option value="">-- انتخاب سازمان --</option>
                  {orgList.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="row" style={{ gap: "var(--s3)" }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="rec-method" className="label t-cap">
                    روش دریافت
                  </label>
                  <select
                    id="rec-method"
                    className="input"
                    value={payMethod}
                    onChange={(e) => {
                      setPayMethod(e.target.value as PaymentMethod);
                    }}
                  >
                    <option value="bank-transfer">واریز به حساب / پایا / ساتنا</option>
                    <option value="card">کارتخوان</option>
                    <option value="cash">نقدی</option>
                    <option value="cheque">چک صیادی</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="rec-date" className="label t-cap">
                    تاریخ دریافت
                  </label>
                  <input
                    id="rec-date"
                    type="date"
                    className="input"
                    value={payDate}
                    onChange={(e) => {
                      setPayDate(e.target.value);
                    }}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="rec-amount" className="label t-cap">
                  مبلغ دریافتی (ریال)
                </label>
                <input
                  id="rec-amount"
                  type="number"
                  className="input"
                  placeholder="مبلغ به ریال"
                  value={payAmount || ""}
                  onChange={(e) => {
                    setPayAmount(Number(e.target.value));
                  }}
                />
                {payAmount > 0 && (
                  <p className="hint muted" style={{ marginTop: "4px" }}>
                    معادل: {formatMoney(payAmount, "toman")}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="rec-ref" className="label t-cap">
                  شماره پیگیری / شماره ارجاع فیش
                </label>
                <input
                  id="rec-ref"
                  type="text"
                  className="input"
                  placeholder="مثال: ۱۰۴۸۹۲۲"
                  value={payRef}
                  onChange={(e) => {
                    setPayRef(e.target.value);
                  }}
                />
              </div>

              {payMethod === "cheque" && (
                <div className="card stack" style={{ gap: "var(--s2)", backgroundColor: "var(--bg-subtle)" }}>
                  <h3 className="t-caption bold">اطلاعات چک صیادی</h3>
                  <div className="row" style={{ gap: "var(--s2)" }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="chk-num" className="label t-cap">
                        شماره صیادی / سریال چک
                      </label>
                      <input
                        id="chk-num"
                        type="text"
                        className="input"
                        placeholder="۱۶ رقمی صیاد"
                        value={chequeNum}
                        onChange={(e) => {
                          setChequeNum(e.target.value);
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="chk-bank" className="label t-cap">
                        نام بانک صادرکننده
                      </label>
                      <input
                        id="chk-bank"
                        type="text"
                        className="input"
                        placeholder="مثال: ملی، ملت، صادرات"
                        value={chequeBank}
                        onChange={(e) => {
                          setChequeBank(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="chk-due" className="label t-cap">
                      تاریخ سررسید چک
                    </label>
                    <input
                      id="chk-due"
                      type="date"
                      className="input"
                      value={chequeDue}
                      onChange={(e) => {
                        setChequeDue(e.target.value);
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="rec-notes" className="label t-cap">
                  توضیحات (اختیاری)
                </label>
                <input
                  id="rec-notes"
                  type="text"
                  className="input"
                  placeholder="توضیحات تکمیلی دریافت"
                  value={payNotes}
                  onChange={(e) => {
                    setPayNotes(e.target.value);
                  }}
                />
              </div>

              <ErrorLine error={recordAction.error} />
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRecordModal(false);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={!payOrgId || payAmount <= 0 || !payDate || recordAction.busy}
                onClick={() => {
                  recordAction.run({
                    organizationId: payOrgId,
                    amount: payAmount as Rial,
                    method: payMethod,
                    paidAt: payDate,
                    referenceNumber: payRef || undefined,
                    notes: payNotes || undefined,
                    chequeNumber: payMethod === "cheque" ? chequeNum : undefined,
                    chequeBank: payMethod === "cheque" ? chequeBank : undefined,
                    chequeDueDate: payMethod === "cheque" ? chequeDue : undefined,
                  });
                }}
              >
                {recordAction.busy ? "در حال ثبت..." : "تأیید و ثبت دریافت"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cheque Bounce */}
      {bouncingCheque && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "500px", width: "90%" }}>
            <h2 className="t-title" style={{ color: "var(--coral-strong)" }}>
              ثبت برگشت چک
            </h2>
            <p className="muted t-cap">
              برگشت چک سبب بازگشت بدهی سازمان و ثبت ردیف برگشتی در صورتحساب می‌شود.
            </p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <p>
                مبلغ چک: <span className="bold">{formatMoney(bouncingCheque.amount, "rial")}</span>
              </p>
              <p>
                شماره چک: <span className="bold">{bouncingCheque.chequeNumber ?? "—"}</span> (
                {bouncingCheque.chequeBank ?? "بانک"})
              </p>

              <div>
                <label htmlFor="bnc-reason" className="label t-cap">
                  علت برگشت چک (اجباری)
                </label>
                <input
                  id="bnc-reason"
                  type="text"
                  className="input"
                  placeholder="مثال: عدم موجودی حساب، نقص امضا، مغایرت تاریخ"
                  value={bounceReason}
                  onChange={(e) => {
                    setBounceReason(e.target.value);
                  }}
                />
              </div>

              <ErrorLine error={chequeStatusAction.error} />
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setBouncingCheque(null);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="urgent"
                disabled={!bounceReason || chequeStatusAction.busy}
                onClick={() => {
                  chequeStatusAction.run({
                    id: bouncingCheque.id,
                    input: {
                      version: bouncingCheque.version,
                      action: "bounce",
                      reason: bounceReason,
                    },
                  });
                }}
              >
                {chequeStatusAction.busy ? "در حال ثبت..." : "تأیید برگشت چک"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Allocate Payment to Invoices */}
      {allocatingPayment && (
        <div className="modal-backdrop">
          <div className="card modal-card" style={{ maxWidth: "700px", width: "95%" }}>
            <h2 className="t-title">تخصیص وجه به فاکتورهای صادرشده</h2>
            <p className="muted t-cap">
              تخصیص مبلغ دریافتی به فاکتورهای باز {getOrgName(allocatingPayment.organizationId)}
            </p>

            <div className="stack" style={{ gap: "var(--s3)", margin: "var(--s4) 0" }}>
              <div className="row wrap-between card" style={{ backgroundColor: "var(--bg-subtle)" }}>
                <div>
                  <span className="muted t-cap">کل مبلغ پرداخت: </span>
                  <span className="bold">{formatMoney(allocatingPayment.amount, "rial")}</span>
                </div>
                <div>
                  <span className="muted t-cap">مانده قابل تخصیص: </span>
                  <span className="bold" style={{ color: "var(--blue-ink)" }}>
                    {formatMoney(allocatingPayment.unallocatedAmount, "rial")}
                  </span>
                </div>
              </div>

              {openInvoicesQuery.isLoading && <p className="muted">در حال بارگذاری فاکتورهای باز...</p>}
              {openInvoicesQuery.data && openInvoicesQuery.data.length === 0 && (
                <p className="muted text-center">هیچ فاکتور صادرشده‌ای برای این سازمان موجود نیست.</p>
              )}

              {openInvoicesQuery.data && openInvoicesQuery.data.length > 0 && (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <table className="table" style={{ width: "100%", fontSize: "13px" }}>
                    <thead>
                      <tr>
                        <th>شماره فاکتور</th>
                        <th>دوره مصرف</th>
                        <th>مبلغ کل</th>
                        <th>مبلغ تخصیص (ریال)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openInvoicesQuery.data.map((inv) => (
                        <tr key={inv.id}>
                          <td className="bold">{faDigits(inv.number)}</td>
                          <td>
                            {formatDate(inv.periodStart)} تا {formatDate(inv.periodEnd)}
                          </td>
                          <td className="bold">{formatMoney(inv.totalAmount, "rial")}</td>
                          <td>
                            <input
                              type="number"
                              className="input"
                              style={{ width: "140px" }}
                              placeholder="۰"
                              value={allocAmounts[inv.id] || ""}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                setAllocAmounts((prev) => ({ ...prev, [inv.id]: val }));
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <ErrorLine error={allocateAction.error} />
            </div>

            <div className="row wrap" style={{ justifyContent: "flex-end", gap: "var(--s3)" }}>
              <Button
                variant="ghost"
                onClick={() => {
                  setAllocatingPayment(null);
                }}
              >
                انصراف
              </Button>
              <Button
                variant="primary"
                disabled={allocateAction.busy}
                onClick={() => {
                  const allocations = Object.entries(allocAmounts)
                    .filter(([, amt]) => amt > 0)
                    .map(([invoiceId, amount]) => ({ invoiceId, amount: amount as Rial }));

                  if (allocations.length === 0) return;

                  allocateAction.run({
                    paymentId: allocatingPayment.id,
                    version: allocatingPayment.version,
                    allocations,
                  });
                }}
              >
                {allocateAction.busy ? "در حال تخصیص..." : "تأیید و اعمال تخصیص"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
